import {
  escapeAttribute,
  HREF,
  readAttribute,
  SAFE_HREF,
  stripRawContent,
} from './html-sanitize-core';

/**
 * Sanitizing real email HTML.
 *
 * Separate from `sanitizeRichText` because the two answer different questions.
 * `sanitizeRichText` guards a vocabulary *we* emit — the handful of tags a Tiptap editor
 * produces — so anything outside it is a forgery and gets dropped. Email HTML is written
 * by strangers, in twenty years of accumulated table layouts, and dropping everything
 * unfamiliar turns a supplier's quote into an unreadable run of words. So this keeps a
 * much larger vocabulary and is correspondingly stricter about attributes.
 *
 * What it will not do, and why each one matters:
 *
 * - **No `style` attribute, no `<style>` element.** CSS reaches outside its element
 *   (`position: fixed`, `url()`, `@import`) and is a clickjacking surface in a panel the
 *   user is about to act on.
 * - **No `on*` handlers, no `javascript:`/`data:` URLs.** `SAFE_HREF` is an allow-list,
 *   so a scheme nobody has heard of yet is refused by default.
 * - **No remote images.** An `<img src>` pointing off-site is a read receipt the sender
 *   gets for free, and the user did not agree to that by opening a thread. `src` is
 *   dropped and `alt` is kept, so the reader sees that an image was there.
 * - **No `<form>`, `<input>`, `<button>`.** A credential prompt rendered inside the app's
 *   own chrome is the most convincing phishing surface there is.
 * - **`target`/`rel` are rewritten, never read.** Every surviving link opens in a new tab
 *   with `noopener noreferrer`, whatever the source asked for.
 *
 * DOM-free, so it runs at ingest inside a Worker as well as in the browser. Applied on
 * write (producing the stored `sanitized_html` variant) *and* on render, because a stored
 * row is only as clean as the sanitizer that was current when it was written.
 */

/**
 * Structural and formatting tags real email uses. Tables are in because email layout is
 * tables — dropping them reflows a decade of newsletters into nonsense.
 */
const ALLOWED_TAGS = new Set([
  'p',
  'div',
  'span',
  'br',
  'hr',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'strike',
  'sub',
  'sup',
  'small',
  'a',
  'ul',
  'ol',
  'li',
  'dl',
  'dt',
  'dd',
  'blockquote',
  'pre',
  'code',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'caption',
  'colgroup',
  'col',
  'figure',
  'figcaption',
  'img',
  'center',
  'font',
  'address',
  'abbr',
  'cite',
  'q',
  'time',
  'mark',
  'wbr',
]);

/** Tags with no closing tag, so a `</td>`-style close would be wrong to emit. */
const VOID_TAGS = new Set(['br', 'hr', 'img', 'col', 'wbr']);

const ALT = /\balt\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;

/** Table geometry worth keeping: it is layout, carries no script, and cannot escape the cell. */
const COLSPAN = /\bcolspan\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;
const ROWSPAN = /\browspan\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;
const SMALL_INTEGER = /^\d{1,3}$/;

function openTag(tag: string, attributes: string): string {
  if (tag === 'a') {
    const href = readAttribute(attributes, HREF);
    if (!href || !SAFE_HREF.test(href)) return '<a>';
    // `rel` is asserted, not inherited: a source-supplied `rel` is exactly what an
    // attacker would clear.
    return `<a href="${escapeAttribute(href)}" target="_blank" rel="noopener noreferrer">`;
  }

  if (tag === 'img') {
    // The `src` never survives, so nothing is fetched and no open is reported back to the
    // sender. The alt text is kept so the reader knows an image was here.
    const alt = escapeAttribute(readAttribute(attributes, ALT).replace(/[<>]/g, ''));
    return alt ? `<img alt="${alt}">` : '<img alt="">';
  }

  if (tag === 'td' || tag === 'th') {
    const spans: string[] = [];
    const colspan = readAttribute(attributes, COLSPAN);
    if (SMALL_INTEGER.test(colspan)) spans.push(`colspan="${colspan}"`);
    const rowspan = readAttribute(attributes, ROWSPAN);
    if (SMALL_INTEGER.test(rowspan)) spans.push(`rowspan="${rowspan}"`);
    return spans.length ? `<${tag} ${spans.join(' ')}>` : `<${tag}>`;
  }

  return `<${tag}>`;
}

/**
 * Strips email HTML down to a safe, readable subset.
 *
 * Every attribute is dropped unless this function rebuilds it explicitly, so a new
 * attribute in a future email cannot arrive by default — the failure mode is a lost
 * `colspan`, not an executed handler.
 */
export function sanitizeEmailHtml(html: string): string {
  const stripped = stripRawContent(html);

  return stripped.replace(
    /<\/?\s*([a-z][\w-]*)([^>]*)>/gi,
    (raw, rawTag: string, attributes: string) => {
      const tag = rawTag.toLowerCase();
      if (!ALLOWED_TAGS.has(tag)) return '';

      const isClosing = raw.trimStart().startsWith('</');
      if (isClosing) return VOID_TAGS.has(tag) ? '' : `</${tag}>`;

      return openTag(tag, attributes);
    }
  );
}

/**
 * Plain text from email HTML, for a preview, a search index, or a model projection.
 *
 * Block-level tags become line breaks first — collapsing a table into one run of words
 * loses the structure that made it readable, and a model reading the result would see a
 * different message than the user does.
 */
export function emailHtmlToPlainText(html: string): string {
  return sanitizeEmailHtml(html)
    .replace(/<\/(p|div|tr|li|h[1-6]|blockquote|table|dd|dt)\s*>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/t[dh]\s*>/gi, '\t')
    .replace(/<[^>]*>/g, '')
    .replace(/&(?:nbsp|#160);/gi, ' ')
    .replace(/&(?:amp|#38);/gi, '&')
    .replace(/&(?:lt|#60);/gi, '<')
    .replace(/&(?:gt|#62);/gi, '>')
    .replace(/&(?:quot|#34);/gi, '"')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
